import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useEvent } from '../contexts/EventContext';
import { Calendar, LogIn } from 'lucide-react';

// ── Traductions ────────────────────────────────────────────────────────────────
const LANGS = {
  fr: {
    join:        'Je m\'inscris ou me connecte',
    howItWorks:  'Comment ça marche ?',
    partners:    'Nos partenaires',
    poweredBy:   'Propulsé par',
    steps: [
      { title: 'Créez un compte ou accédez à votre espace',     desc: 'Connectez-vous avec le code reçu par email.' },
      { title: 'Valorisez votre profil',  desc: 'Complétez vos informations (photos, téléphone, enjeux...) pour faciliter les prises de contact' },
      { title: 'Identifiez les bons contacts et obtenez des matches', desc: 'Parcourez la liste des participant·es selon leurs enjeux et leur type d’entreprise.' },
      { title: 'Entrez en relation et planifiez des RDV',   desc: 'Accédez aux coordonnées pour organiser vos échanges pendant ou après l\'événement' },
    ],
  },
  en: {
    join:        'Connect or create an account',
    howItWorks:  'How it works',
    partners:    'Our partners',
    poweredBy:   'Powered by',
    steps: [
      { title: 'Create an account',    desc: 'Sign up with your professional information' },
      { title: 'Define your profile',  desc: 'Specify what you offer and what you\'re looking for' },
      { title: 'Get matches',          desc: 'Receive relevant partner suggestions' },
      { title: 'Schedule meetings',    desc: 'Request and confirm meetings at the event' },
    ],
  },
  de: {
    join:        'Ich nehme teil',
    howItWorks:  'Wie es funktioniert',
    partners:    'Unsere Partner',
    poweredBy:   'Bereitgestellt von',
    steps: [
      { title: 'Konto erstellen',    desc: 'Registrieren Sie sich mit Ihren beruflichen Daten' },
      { title: 'Profil definieren',  desc: 'Geben Sie Ihr Angebot und Ihren Bedarf an' },
      { title: 'Matches erhalten',   desc: 'Erhalten Sie relevante Partnervorschläge' },
      { title: 'Meetings planen',    desc: 'Beantragen und bestätigen Sie Meetings auf der Veranstaltung' },
    ],
  },
};

// Détection de la langue du navigateur (fr/en/de, fallback fr)
const detectLang = () => {
  const bl = (navigator.language || 'fr').slice(0, 2).toLowerCase();
  return ['fr', 'en', 'de'].includes(bl) ? bl : 'fr';
};

// ── Composant ──────────────────────────────────────────────────────────────────
const EventLandingPage = () => {
  const { event, loading, loadEventBySlug } = useEvent();
  const { slug } = useParams();
  const navigate  = useNavigate();

  const [lang,     setLang]     = useState(detectLang);
  const [sponsors, setSponsors] = useState([]);

  const t = LANGS[lang] || LANGS.fr;

  // Charger l'événement si besoin
  useEffect(() => {
    if (slug && (!event || event.slug !== slug)) {
      loadEventBySlug(slug);
    }
  }, [slug]);

  // Charger les sponsors une fois l'événement connu
  useEffect(() => {
    if (!event?.slug) return;
    fetch(`/api/sponsors/${encodeURIComponent(event.slug)}`)
      .then(r => r.json())
      .then(d => { if (d.success) setSponsors(d.data || []); })
      .catch(() => {});
  }, [event?.slug]);

  // ── Loading ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading…</p>
        </div>
      </div>
    );
  }

  if (!event) return null;

  // ── Couleurs dynamiques ────────────────────────────────────────────────────
  const colorMain      = event.color_main      || '#1c1b3a';
  const colorSecondary = event.color_secondary || colorMain;
  const colorStripe    = event.color_stripe    || '#e20714';

  const logoForDarkBg  = event.logo_light || event.logo_dark || event.logo;
  const logoForLightBg = event.logo_dark  || event.logo;

  const handleLogin = () => navigate(`/event/${event.slug}/login`);
  const isAccessOpen = event.access !== '0';

  const formatDate = (iso) => {
    if (!iso) return null;
    try {
      return new Date(iso).toLocaleDateString(lang === 'de' ? 'de-DE' : lang === 'en' ? 'en-GB' : 'fr-FR', {
        day: 'numeric', month: 'long', year: 'numeric',
      });
    } catch { return iso; }
  };

  // ── Rendu ──────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#f5f5f7' }}>

      {/* Barre stripe colorée */}
      <div style={{ height: 4, backgroundColor: colorStripe }} />

      {/* ── Header ── */}
      <header style={{ backgroundColor: colorMain }}>
        <div className="max-w-7xl mx-auto px-4 py-3 flex justify-between items-center">
          {/* Logo */}
          {logoForDarkBg
            ? <img src={logoForDarkBg} alt={event.name} className="h-10 object-contain" />
            : <span className="text-white font-bold text-xl">{event.name}</span>
          }

          {/* Sélecteur de langue */}
          <div className="flex items-center gap-1">
            {['fr', 'en', 'de'].map(l => (
              <button
                key={l}
                onClick={() => setLang(l)}
                className={`px-2.5 py-1 rounded text-sm font-semibold transition ${
                  lang === l
                    ? 'text-white'
                    : 'text-white/50 hover:text-white/80'
                }`}
                style={lang === l ? { backgroundColor: `${colorStripe}` } : {}}
              >
                {l.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
      </header>

      {/* ── Hero ── */}
      <section className="flex-grow max-w-7xl mx-auto w-full px-4 py-10">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          <div className="grid md:grid-cols-2">

            {/* Gauche — infos + CTA */}
            <div className="p-10 flex flex-col justify-center">

              {logoForLightBg && (
                <div className="mb-6">
                  <img src={logoForLightBg} alt={event.name} className="max-h-20 object-contain" />
                </div>
              )}

              <h1 className="text-3xl font-bold mb-3" style={{ color: colorMain }}>
                {event.name}
              </h1>

              {event.description && (
                <p className="text-gray-600 text-base mb-6 leading-relaxed">
                  {event.description}
                </p>
              )}

              {/* Dates */}
              {event.start_date && (
                <div className="flex items-center gap-3 text-gray-700 mb-8">
                  <Calendar size={20} style={{ color: colorMain }} />
                  <span className="text-sm font-medium">
                    {formatDate(event.start_date)}
                    {event.end_date && event.end_date !== event.start_date && (
                      <> → {formatDate(event.end_date)}</>
                    )}
                  </span>
                </div>
              )}

              {/* Bouton unique */}
              <button
                onClick={isAccessOpen ? handleLogin : undefined}
                disabled={!isAccessOpen}
                className={`w-full px-6 py-4 rounded-xl text-base font-semibold transition flex items-center justify-center gap-2 shadow-lg ${
                  isAccessOpen
                    ? 'text-white hover:opacity-90 cursor-pointer'
                    : 'text-gray-400 cursor-not-allowed'
                }`}
                style={{ backgroundColor: isAccessOpen ? colorMain : '#e5e7eb' }}
              >
                <LogIn size={18} />
                {t.join}
              </button>

              {/* Message d'accès fermé */}
              {!isAccessOpen && event.access_message && (
                <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-xl text-center">
                  <p className="text-sm text-amber-800 font-medium">{event.access_message}</p>
                </div>
              )}
            </div>

            {/* Droite — Banner ou gradient */}
            <div
              className="hidden md:flex flex-col justify-center items-center relative overflow-hidden min-h-80"
              style={event.banner_url ? {} : {
                background: `linear-gradient(135deg, ${colorMain} 0%, ${colorSecondary}cc 100%)`
              }}
            >
              {event.banner_url ? (
                <img
                  src={event.banner_url}
                  alt={event.name}
                  className="w-full h-full object-contain"
                />
              ) : (
                <div className="text-center text-white px-8">
                  {logoForDarkBg && (
                    <img src={logoForDarkBg} alt={event.name} className="max-h-16 mx-auto mb-6 object-contain" />
                  )}
                  <p className="opacity-80 text-lg">{event.description || 'B2B Matchmaking'}</p>
                </div>
              )}
            </div>

          </div>
        </div>
      </section>

      {/* ── Comment ça marche ── */}
      <section className="max-w-7xl mx-auto w-full px-4 pb-10">
        <div className="bg-white rounded-2xl shadow-lg p-10">
          <h2 className="text-2xl font-bold text-center mb-10" style={{ color: colorMain }}>
            {t.howItWorks}
          </h2>

          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-8">
            {t.steps.map(({ title, desc }, i) => (
              <div key={i} className="text-center">
                <div
                  className="w-14 h-14 rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-3 text-white"
                  style={{ backgroundColor: i % 2 === 0 ? colorMain : colorStripe }}
                >
                  {i + 1}
                </div>
                <h3 className="font-bold text-gray-900 mb-1 text-sm">{title}</h3>
                <p className="text-xs text-gray-500 leading-snug">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Sponsors ── */}
      {sponsors.length > 0 && (
        <section className="max-w-7xl mx-auto w-full px-4 pb-10">
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <h2 className="text-lg font-bold text-center mb-6" style={{ color: colorMain }}>
              {t.partners}
            </h2>
            <div className="flex flex-wrap justify-center items-center gap-6">
              {sponsors.map((s, i) => (
                <img
                  key={`${s.id}-${i}`}
                  src={s.url}
                  alt={s.alt}
                  className="max-h-20 max-w-xs object-contain"
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── Footer ── */}
      <footer style={{ backgroundColor: colorMain }} className="py-5 mt-auto">
        <div className="max-w-7xl mx-auto px-4 flex flex-col sm:flex-row items-center justify-between gap-3">
          {logoForDarkBg && (
            <img src={logoForDarkBg} alt={event.name} className="h-7 object-contain opacity-80" />
          )}
          <p className="text-sm text-white/50">
            {t.poweredBy} <span className="font-semibold text-white">Mobivent</span>
          </p>
        </div>
      </footer>

    </div>
  );
};

export default EventLandingPage;
